import { formatDistanceToNow } from 'date-fns'
import { useState } from 'react'
import { useUnsubscribedContacts } from '@/api/hooks/use-audiences'
import { DataTable } from '@/components/ui/data-table'
import { AudienceUnsubReasons } from '@/pages/audiences/audience-unsub-reasons'
import type { UnsubscribedContact } from '@/types/newsletter'

interface AudienceUnsubTabProps {
  audienceId: string
}

export function AudienceUnsubTab({ audienceId }: AudienceUnsubTabProps): React.JSX.Element {
  const [page, setPage] = useState(1)

  const { data, isLoading } = useUnsubscribedContacts(audienceId, {
    page,
    per_page: 25,
  })

  const contacts = data?.data ?? []
  const pagination = data?.pagination
  const distribution = data?.reason_distribution ?? []

  const columns = [
    {
      key: 'email',
      label: 'Email',
      render: (row: UnsubscribedContact) => <span className="font-medium">{row.email}</span>,
    },
    {
      key: 'unsubscribed_at',
      label: 'Date',
      render: (row: UnsubscribedContact) => (
        <span className="text-sm text-text-secondary">
          {formatDistanceToNow(new Date(row.unsubscribed_at), { addSuffix: true })}
        </span>
      ),
    },
    {
      key: 'unsub_reason',
      label: 'Reason',
      render: (row: UnsubscribedContact) => (
        <span className="text-sm text-text-secondary">{row.unsub_reason ?? '-'}</span>
      ),
    },
    {
      key: 'unsub_feedback',
      label: 'Feedback',
      render: (row: UnsubscribedContact) => (
        <span
          className="max-w-[250px] truncate text-sm text-text-secondary"
          title={row.unsub_feedback ?? undefined}
        >
          {row.unsub_feedback ?? '-'}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Count summary */}
      {pagination && (
        <p className="text-sm text-text-secondary">
          {pagination.total.toLocaleString()} unsubscribed in the last 90 days
        </p>
      )}

      {/* Reason distribution */}
      {distribution.length > 0 && <AudienceUnsubReasons distribution={distribution} />}

      {/* Unsubscribed contacts list */}
      <DataTable
        columns={columns}
        data={contacts}
        isLoading={isLoading}
        emptyMessage="No unsubscribes in the last 90 days"
        pagination={
          pagination
            ? { page: pagination.page, perPage: pagination.per_page, total: pagination.total }
            : undefined
        }
        onPageChange={setPage}
      />
    </div>
  )
}
